/**
 * 教学场景计算 —— 自 scenario.js 平移，逻辑未改。
 * 所有 facelet 状态都通过 cubejs 真实应用 move 序列得到，保证状态合法可达。
 */
// 注意：直接引 lib/cube.js（纯 CJS，只含状态模型）。包入口 index.js 会连带
// async.js，其顶层 `this.Cube` 在 Vite(rolldown) 的严格 ESM 下抛错，且求解器用不到。
import Cube from 'cubejs/lib/cube.js';
import { SOLVED, FACE_OFFSET, type Face } from './cube';

export interface DemoCase {
  id: string;
  label: string;
  brief?: string;
  setup?: string;
  formula: string;
  notes?: string[];
}

export interface Scenario {
  start: string;
  goal: string;
  setupMoves: string[];
  formulaMoves: string[];
}

/** 计算教学场景：start = SOLVED + setup；goal = start + formula */
export function computeScenario(demo: { setup?: string; formula?: string }): Scenario {
  const setupMoves = (demo.setup || '').trim().split(/\s+/).filter(Boolean);
  const formulaMoves = (demo.formula || '').trim().split(/\s+/).filter(Boolean);

  const startCube = Cube.fromString(SOLVED);
  if (setupMoves.length) startCube.move(setupMoves.join(' '));
  const start = startCube.asString();

  const goalCube = Cube.fromString(start);
  if (formulaMoves.length) goalCube.move(formulaMoves.join(' '));
  const goal = goalCube.asString();

  return { start, goal, setupMoves, formulaMoves };
}

/** 一组面的全部棱块索引（每面 1,3,5,7 位） */
export function edgeIndices(faces: Face[]): number[] {
  return faces.flatMap((f) => [1, 3, 5, 7].map((i) => FACE_OFFSET[f] + i));
}

/** 一组面的全部角块索引（每面 0,2,6,8 位） */
export function cornerIndices(faces: Face[]): number[] {
  return faces.flatMap((f) => [0, 2, 6, 8].map((i) => FACE_OFFSET[f] + i));
}

/** 一组面的全部索引（9 个） */
export function faceAllIndices(faces: Face[]): number[] {
  return faces.flatMap((f) => Array.from({ length: 9 }, (_, i) => FACE_OFFSET[f] + i));
}

/** goal.highlight 标识符 → facelet 索引数组（自 learn.js 平移） */
export function resolveHighlight(id: string | number[] | undefined): number[] {
  if (!id) return [];
  if (Array.isArray(id)) return id;
  switch (id) {
    case 'D_EDGES':
      return edgeIndices(['D']);
    case 'D_CORNERS':
      return cornerIndices(['D']);
    case 'D_ALL':
      return faceAllIndices(['D']);
    case 'U_EDGES':
      return edgeIndices(['U']);
    case 'U_CORNERS':
      return cornerIndices(['U']);
    case 'U_ALL':
      return faceAllIndices(['U']);
    case 'MIDDLE_EDGES': {
      const out: number[] = [];
      for (const f of ['F', 'R', 'B', 'L'] as Face[]) {
        [3, 5].forEach((i) => out.push(FACE_OFFSET[f] + i));
      }
      return out;
    }
    default:
      return [];
  }
}
